import {
  EstateType,
  FilterParams,
  ExtraCriterias,
  FilterTypes,
  ServicesTitles,
  SortType,
  RangeTagNames,
} from './Enums';
import { makeCamelCase } from './helpers';
import {
  getBrokerName,
  getCountyTag,
  getMunicipalityTag,
  getOfficeName,
} from './services/api';
import { ForSaleStrings } from './types/ForSaleString';
import { ITag } from './types/interfaces';
import { createContext } from 'react';

// Collects params from query and set tags from those values
export async function getFilterFromQueryParams(): Promise<ITag[]> {
  const tags: ITag[] = [];
  const params = new URLSearchParams(window.location.search);

  // estate types
  const estateTypeParam = params.get(FilterParams.ESTATETYPE_PARAM);
  estateTypeParam?.split(',').forEach((estateType) => {
    tags.push({
      type: 'estateType',
      estateType: estateType as EstateType,
    });
  });

  const municiParam = params.get(FilterParams.MUNICI_PARAM);
  municiParam?.split(',').forEach((municipality) => {
    tags.push({
      type: 'munici',
      id: municipality,
      label: `${municipality} (Kommun)`,
    });
  });

  const countyParam = params.get(FilterParams.COUNTY_PARAM);
  countyParam?.split(',').forEach((county) => {
    tags.push({ type: 'county', id: county, label: `${county} (Län)` });
  });

  const areaParam = params.get(FilterParams.AREA_PARAM);
  const areaNames = params.get('areaNames')?.split(',');
  areaParam?.split(',').forEach((area, i) => {
    const areaNameFromQP = areaNames?.[i] ?? '';
    tags.push({
      type: 'area',
      id: area,
      label: `${areaNameFromQP ? areaNameFromQP : area} (Område)`,
    });
  });

  //this is done to set button when page is reloaded
  const polyParam = params.get(FilterParams.POLYGON_PARAM);
  if (polyParam !== null) {
    tags.push({
      type: FilterParams.POLYGON_PARAM,
      label: 'Ritat område',
      polygons: polyParam.toString(),
    });
  }

  // extra criterias
  const extraCriteriaParam = params.get(FilterParams.EXTRACRITERIA_PARAM);
  extraCriteriaParam?.split(',').forEach((id) => {
    const criteriaName = getExtraSearchCriteriaName(id);
    tags.push({ type: 'extraCriteria', id, text: criteriaName });
  });

  function getExtraSearchCriteriaName(id: string) {
    const criteriaResp = ExtraCriterias[id];
    const criteriaName = criteriaResp?.text;
    return criteriaName;
  }

  // showOnlyHeadStart
  const fromheadStartQuery = params.get(FilterParams.HEADSTART_PARAM);
  if (
    fromheadStartQuery !== null &&
    (FilterParams.BROKERID_PARAM !== null ||
      FilterParams.CUSTOMERID_PARAM !== null)
  ) {
    const fromheadStartParam = fromheadStartQuery === 'true' ? true : false;
    tags.push({
      type: FilterParams.HEADSTART_PARAM,
      isChecked: fromheadStartParam,
    });
  }

  // showSold
  const fromSoldQuery = params.get(FilterParams.SOLD_PARAM);
  if (
    fromSoldQuery !== null &&
    (FilterParams.BROKERID_PARAM !== null ||
      FilterParams.CUSTOMERID_PARAM !== null)
  ) {
    const soldParam = fromSoldQuery === 'true' ? true : false;
    tags.push({ type: FilterParams.SOLD_PARAM, isChecked: soldParam });
  }

  // alignment
  const alignmentsParam = params.get(FilterParams.ALIGNMENT_PARAM);
  alignmentsParam?.split(',').forEach((id) => {
    id = id.replace('/', '-');
    tags.push({ type: 'alignment', id });
  });

  //Broker param from offices
  const brokerParam = params.get(FilterParams.BROKERID_PARAM);
  if (brokerParam !== null) {
    const brokerName = await getBrokerName(brokerParam);
    if (brokerName !== undefined) {
      tags.push({
        type: 'broker',
        id: brokerParam,
        label: brokerName,
      });
    }
  }

  //Office id from officepage
  const officeParam = params.get(FilterParams.CUSTOMERID_PARAM);
  if (officeParam !== null) {
    const officeName = await getOfficeName(officeParam);
    tags.push({ type: 'office', id: officeParam, label: officeName });
  }

  //Sustainable choice
  const greenEstateParam = params.get(FilterParams.GREENESTATES_PARAM);
  if (greenEstateParam !== null) {
    tags.push({
      type: FilterParams.GREENESTATES_PARAM,
      label: ServicesTitles.GreenEstate,
      isChecked: greenEstateParam === 'true' ? true : false,
    });
  }
  //Bidding
  const biddingParam = params.get(FilterParams.BIDDING_PARAM);
  if (biddingParam !== null) {
    tags.push({
      type: FilterParams.BIDDING_PARAM,
      label: ForSaleStrings.BiddingShort,
      isChecked: biddingParam === 'true' ? true : false,
    });
  }
  //sorting
  const sortParam = params.get(FilterParams.SORT_PARAM);
  if (sortParam !== null) {
    tags.push({
      type: 'sortType',
      id: sortParam,
      name: SortType[parseInt(sortParam)].label,
    });
  }

  // price
  if (
    params.get(FilterParams.PRICEMIN_PARAM) !== null &&
    params.get(FilterParams.PRICEMAX_PARAM) !== null
  ) {
    const priceMinParam = Number(params.get(FilterParams.PRICEMIN_PARAM));
    const priceMaxParam = Number(params.get(FilterParams.PRICEMAX_PARAM));
    tags.push({
      type: 'rangeTag',
      name: RangeTagNames.Price,
      minVal: priceMinParam,
      maxVal: priceMaxParam,
    });
  }

  // rooms
  if (
    params.get(FilterParams.ROOMMIN_PARAM) !== null &&
    params.get(FilterParams.ROOMMAX_PARAM) !== null
  ) {
    const roomMinParam = Number(params.get(FilterParams.ROOMMIN_PARAM));
    const roomMaxParam = Number(params.get(FilterParams.ROOMMAX_PARAM));
    tags.push({
      type: 'rangeTag',
      name: RangeTagNames.Room,
      minVal: roomMinParam,
      maxVal: roomMaxParam,
    });
  }

  // livingspace
  if (
    params.get(FilterParams.LIVINGSPACEMIN_PARAM) !== null &&
    params.get(FilterParams.LIVINGSPACEMAX_PARAM) !== null
  ) {
    const livingSpaceMinParam = Number(
      params.get(FilterParams.LIVINGSPACEMIN_PARAM)
    );
    const livingSpaceMaxParam = Number(
      params.get(FilterParams.LIVINGSPACEMAX_PARAM)
    );
    tags.push({
      type: 'rangeTag',
      name: RangeTagNames.LivingSpace,
      minVal: livingSpaceMinParam,
      maxVal: livingSpaceMaxParam,
    });
  }

  // monthly cost
  if (
    params.get(FilterParams.MONTHLYCOSTMIN_PARAM) !== null &&
    params.get(FilterParams.MONTHLYCOSTMAX_PARAM) !== null
  ) {
    const monthlyCostMinParam = Number(
      params.get(FilterParams.MONTHLYCOSTMIN_PARAM)
    );
    const monthlyCostMaxParam = Number(
      params.get(FilterParams.MONTHLYCOSTMAX_PARAM)
    );
    tags.push({
      type: 'rangeTag',
      name: RangeTagNames.Charge,
      minVal: monthlyCostMinParam,
      maxVal: monthlyCostMaxParam,
    });
  }

  // plotarea
  if (
    params.get(FilterParams.PLOTAREAMIN_PARAM) !== null &&
    params.get(FilterParams.PLOTAREAMAX_PARAM) !== null
  ) {
    const plotAreaMinParam = Number(params.get(FilterParams.PLOTAREAMIN_PARAM));
    const plotAreaMaxParam = Number(params.get(FilterParams.PLOTAREAMAX_PARAM));
    tags.push({
      type: 'rangeTag',
      name: RangeTagNames.PlotArea,
      minVal: plotAreaMinParam,
      maxVal: plotAreaMaxParam,
    });
  }

  return tags;
}

//create querystring from our tags
export function filterToQuerystring(
  filter: ITag[],
  page?: number,
  myPagesCheck?: boolean
): string {
  const filtersStrings = [];

  // page
  if (page != null) {
    filtersStrings.push(`page=${page}`);
  }

  //municipality
  if (filter.some((x) => x.type === 'munici' && !myPagesCheck)) {
    filtersStrings.push(
      `${FilterParams.MUNICI_PARAM}=${filter
        .filter((x) => x.type === 'munici')
        .map((x) => x.id)
        .join(',')}`
    );
  } else if (filter.some((x) => x.type === 'munici' && myPagesCheck)) {
    filtersStrings.push(
      `${FilterParams.MUNICI_PARAM}=${filter
        .filter((x) => x.type === 'munici')
        .map((x) => x.municipality)
        .join(',')}`
    );
  }

  //county
  if (filter.some((x) => x.type === 'county')) {
    filtersStrings.push(
      `${FilterParams.COUNTY_PARAM}=${filter
        .filter((x) => x.type === 'county')
        .map((x) => x.id)
        .join(',')}`
    );
  }

  //area
  if (filter.some((x) => x.type === 'area')) {
    filtersStrings.push(
      `${FilterParams.AREA_PARAM}=${filter
        .filter((x) => x.type === 'area')
        .map((x) => x.id)
        .join(',')}`
    );
    filtersStrings.push(
      `${'areaNames'}=${filter
        .filter((x) => x.type === 'area')
        .map((x) => x.label?.split(' (')[0])
        .join(',')}`
    );
  }

  // estate types
  if (filter.some((x) => x.type === 'estateType')) {
    filtersStrings.push(
      `${FilterParams.ESTATETYPE_PARAM}=${filter
        .filter((x) => x.type === 'estateType')
        .map((x) => x.estateType)
        .join(',')}`
    );
  }

  // extra criterias
  if (filter.some((x) => x.type === 'extraCriteria')) {
    filtersStrings.push(
      `${FilterParams.EXTRACRITERIA_PARAM}=${filter
        .filter((x) => x.type === 'extraCriteria')
        .map((x) => x.id)
        .join(',')}`
    );
  }

  // alignment
  if (filter.some((x) => x.type === 'alignment')) {
    filtersStrings.push(
      `${FilterParams.ALIGNMENT_PARAM}=${filter
        .filter((x) => x.type === 'alignment')
        .map((x) => x.id)
        .join(',')}`
    );
  }

  if (filter.some((x) => x.type === 'broker')) {
    filtersStrings.push(
      `${FilterParams.BROKERID_PARAM}=${filter
        .filter((x) => x.type === 'broker')
        .map((x) => x.id)}`
    );
  }

  if (filter.some((x) => x.type === 'office')) {
    filtersStrings.push(
      `${FilterParams.CUSTOMERID_PARAM}=${filter
        .filter((x) => x.type === 'office')
        .map((x) => x.id)}`
    );
  }

  //Försprång
  if (filter.some((x) => x.type === FilterParams.HEADSTART_PARAM)) {
    filtersStrings.push(
      `${FilterParams.HEADSTART_PARAM}=${filter
        .filter((x) => x.type === FilterParams.HEADSTART_PARAM)
        .map((x) => x.isChecked)}`
    );
  }

  //Only show sold
  if (filter.some((x) => x.type === FilterParams.SOLD_PARAM)) {
    filtersStrings.push(
      `${FilterParams.SOLD_PARAM}=${filter
        .filter((x) => x.type === FilterParams.SOLD_PARAM)
        .map((x) => x.isChecked)}`
    );
  }
  //Sustainable choice
  if (filter.some((x) => x.type === FilterParams.GREENESTATES_PARAM)) {
    filtersStrings.push(
      `${FilterParams.GREENESTATES_PARAM}=${filter
        .filter((x) => x.type === FilterParams.GREENESTATES_PARAM)
        .map((x) => x.isChecked)}`
    );
  }
  //Bidding
  if (filter.some((x) => x.type === FilterParams.BIDDING_PARAM)) {
    filtersStrings.push(
      `${FilterParams.BIDDING_PARAM}=${filter
        .filter((x) => x.type === FilterParams.BIDDING_PARAM)
        .map((x) => x.isChecked)}`
    );
  }
  // price
  if (filter.some((x) => x.type === 'rangeTag' && x.name === 'Pris')) {
    filtersStrings.push(
      `${FilterParams.PRICEMIN_PARAM}=${filter
        .filter((x) => x.type === 'rangeTag' && x.name === 'Pris')
        .map((x) => x.minVal)
        .join(',')}`
    );
  }

  if (filter.some((x) => x.type === 'rangeTag' && x.name === 'Pris')) {
    filtersStrings.push(
      `${FilterParams.PRICEMAX_PARAM}=${filter
        .filter((x) => x.type === 'rangeTag' && x.name === 'Pris')
        .map((x) => x.maxVal)
        .join(',')}`
    );
  }

  //rooms
  if (filter.some((x) => x.type === 'rangeTag' && x.name === 'Rum')) {
    filtersStrings.push(
      `${FilterParams.ROOMMIN_PARAM}=${filter
        .filter((x) => x.type === 'rangeTag' && x.name === 'Rum')
        .map((x) => x.minVal)
        .join(',')}`
    );
  }
  if (filter.some((x) => x.type === 'rangeTag' && x.name === 'Rum')) {
    filtersStrings.push(
      `${FilterParams.ROOMMAX_PARAM}=${filter
        .filter((x) => x.type === 'rangeTag' && x.name === 'Rum')
        .map((x) => x.maxVal)
        .join(',')}`
    );
  }

  // livingspace
  if (filter.some((x) => x.type === 'rangeTag' && x.name === 'Boarea')) {
    filtersStrings.push(
      `${FilterParams.LIVINGSPACEMIN_PARAM}=${filter
        .filter((x) => x.type === 'rangeTag' && x.name === 'Boarea')
        .map((x) => x.minVal)
        .join(',')}`
    );
  }

  if (filter.some((x) => x.type === 'rangeTag' && x.name === 'Boarea')) {
    filtersStrings.push(
      `${FilterParams.LIVINGSPACEMAX_PARAM}=${filter
        .filter((x) => x.type === 'rangeTag' && x.name === 'Boarea')
        .map((x) => x.maxVal)
        .join(',')}`
    );
  }

  // Monthly cost
  if (filter.some((x) => x.type === 'rangeTag' && x.name === 'Avgift')) {
    filtersStrings.push(
      `${FilterParams.MONTHLYCOSTMIN_PARAM}=${filter
        .filter((x) => x.type === 'rangeTag' && x.name === 'Avgift')
        .map((x) => x.minVal)
        .join(',')}`
    );
  }

  if (filter.some((x) => x.type === 'rangeTag' && x.name === 'Avgift')) {
    filtersStrings.push(
      `${FilterParams.MONTHLYCOSTMAX_PARAM}=${filter
        .filter((x) => x.type === 'rangeTag' && x.name === 'Avgift')
        .map((x) => x.maxVal)
        .join(',')}`
    );
  }

  // Plot Area
  if (filter.some((x) => x.type === 'rangeTag' && x.name === 'Tomtarea')) {
    filtersStrings.push(
      `${FilterParams.PLOTAREAMIN_PARAM}=${filter
        .filter((x) => x.type === 'rangeTag' && x.name === 'Tomtarea')
        .map((x) => x.minVal)
        .join(',')}`
    );
  }

  if (filter.some((x) => x.type === 'rangeTag' && x.name === 'Tomtarea')) {
    filtersStrings.push(
      `${FilterParams.PLOTAREAMAX_PARAM}=${filter
        .filter((x) => x.type === 'rangeTag' && x.name === 'Tomtarea')
        .map((x) => x.maxVal)
        .join(',')}`
    );
  }

  if (filter.some((x) => x.type === 'polygons')) {
    filtersStrings.push(
      `${FilterParams.POLYGON_PARAM}=${filter
        .filter((x) => x.type === 'polygons')
        .map((x) => x.polygons.toString())
        .join(',')}`
    );
  }

  // sortType
  if (filter.some((x) => x.type === 'sortType')) {
    filtersStrings.push(
      `${FilterParams.SORT_PARAM}=${filter
        .filter((x) => x.type === 'sortType')
        .map((x) => x.id)}`
    );
  }

  return filtersStrings.length ? encodeURI(`?${filtersStrings.join('&')}`) : '';
}

//Defining Context where all filtering options lives
export const FilterContext = createContext<IFilterContext>(
  {} as IFilterContext
);

export interface IFilterContext {
  tags: ITag[];
  setTags(newVal: ITag[]): void;
  visibleExtraCrits: { [key: string]: ITag };
  setVisibleExtraCrits(newVal: { [key: string]: ITag }): void;
  sessionId?: string;
  user?: string;
  isBigScreen: boolean;
  isMediumScreen: boolean;
  isSmallScreen: boolean;
}

//For SEO purposes
export async function getFilterFromPath(
  path: Record<string, string>
): Promise<ITag[]> {
  const tags: ITag[] = [];
  if (path.area) {
    const cameledArea = makeCamelCase(path.area);
    const withoutDashes = cameledArea.replace(/-/g, ' ');
    tags.push({
      type: 'area',
      id: cameledArea,
      label: `${withoutDashes} (Område)`,
    });
  } else if (path.muni) {
    const cameledMunici = makeCamelCase(path.muni);
    const municipality = makeCamelCase(await getMunicipalityTag(cameledMunici));
    if (municipality !== undefined) {
      tags.push({
        type: 'munici',
        id: cameledMunici,
        label: `${municipality}`,
      });
    }
  } else if (path.county) {
    const cameledCounty = makeCamelCase(path.county);
    const county = makeCamelCase(await getCountyTag(cameledCounty));
    if (county !== undefined) {
      tags.push({
        type: 'county',
        id: cameledCounty,
        label: `${county}`,
      });
    }
  }

  if (
    path.estateType &&
    path.estateType !== 'bostad' &&
    path.estateType !== 'bra-energiklass'
  ) {
    let tempHolder = '';
    let prettyEstateTypeName = path.estateType;
    //ordinary cases
    if (path.estateType === 'bostadsratt') {
      tempHolder = path.estateType;
      prettyEstateTypeName = replaceAt(tempHolder, 8, 'ä');
    } else if (path.estateType === 'gard') {
      tempHolder = path.estateType;
      prettyEstateTypeName = replaceAt(tempHolder, 1, 'å');
    }
    const withoutDashes = prettyEstateTypeName.replace(/-/g, ' ');

    const cameledEstateType = makeCamelCase(withoutDashes);
    tags.push({
      type: 'estateType',
      estateType: cameledEstateType as EstateType,
    });
  } else if (path.estateType && path.estateType === 'bra-energiklass') {
    tags.push({
      type: FilterTypes.ShowGreenEstates,
      isChecked: true,
      label: ServicesTitles.GreenEstate,
    });
  }

  return tags;
}

function replaceAt(myString: string, index: number, replacement: string) {
  return (
    myString.substr(0, index) +
    replacement +
    myString.substr(index + replacement.length)
  );
}
